/* eslint-disable prefer-const */
import React, { SetStateAction } from 'react'
import { createPortal } from 'react-dom'
import { WizardStep } from './wizardStep';
import { PageLockContext } from '../PageLockProvider';
import { LucideX } from 'lucide-react';
import { HelpIcon } from '../ui/Icons/TooltipIcon';
import { useIsMobile } from '@/hooks/use-mobile';
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";

interface WizardProviderProps {
    autoRun?: boolean;
    children: React.ReactNode;
    wizardSteps: WizardStep[];
    wizardCompleted?: () => void;
}

const WizardProvider = ({ children, wizardSteps: initialWizardSteps, wizardCompleted, autoRun }: WizardProviderProps) => {
    const lock = React.useContext(PageLockContext);

    const [wizardCompleteFunctionCall, setWizardCompleteFunctionCall] = React.useState<(() => void)>(wizardCompleted || (() => { }));
    const [guidedTourCompleteFunctionCall, setGuidedTourCompleteFunctionCall] = React.useState<(() => void)>(() => { });
    const [wizardSteps, setWizardSteps] = React.useState<WizardStep[]>(initialWizardSteps);
    const [progressPercentage, setProgressPercentage] = React.useState(0);
    const [currentStep, setCurrentStep] = React.useState<WizardStep>(initialWizardSteps[0]);
    const progressBarColor = "bg-wizard-progress";
    const progressBarBorderColor = "border-wizard-progress";
    const progressBarBackgroundColor = "bg-card";
    const [showWizardPointer, setShowWizardPointer] = React.useState(false);
    const [showMore, setShowMore] = React.useState(false);

    const [isGuidedTour, setIsGuidedTour] = React.useState(false);
    const [tourCurrentStep, setTourCurrentStep] = React.useState(0);

    const [autoplay, setAutoplay] = React.useState(false);
    const autoplayDelay = 5000; // milliseconds

    // Draggable panel state
    const [panelPosition, setPanelPosition] = React.useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);
    const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

    const isMobile = useIsMobile();
    const zoomLevel = useResponsiveZoom();

    // Mouse event handlers for dragging (defined at component level to avoid hook issues)
    const handleMouseMove = React.useCallback((e: MouseEvent) => {
        if (isDragging) {
            setPanelPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    }, [isDragging, dragStart]);

    const handleMouseUp = React.useCallback(() => {
        setIsDragging(false);
    }, []);

    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    React.useEffect(() => {
        if (autoRun) {
            if (progressPercentage === 100) {
                allStepsCompleted();
            } else {
                getNextStep();
            }
        }
    }, [progressPercentage, lock.viewOnly]);

    React.useEffect(() => {
        if (isGuidedTour) {
            if (currentStep.functionCall) {
                currentStep.functionCall();
                // Wait for UI updates (e.g., dialogs opening), then force re-render to update wizardPointer location
                setTimeout(() => {
                    setShowWizardPointer(false);
                    setTimeout(() => setShowWizardPointer(true), 0);
                }, 0); // Adjust delay as needed for your UI
            }

            if (autoplay) {
                // Store timer id so we can clear it if autoplay is stopped
                let timerId: NodeJS.Timeout;
                timerId = setTimeout(() => {
                    if (tourCurrentStep < wizardSteps.length) {
                        setTourCurrentStep(tourCurrentStep + 1);
                        const nextStep = wizardSteps[tourCurrentStep];
                        if (nextStep) {
                            setCurrentStep(nextStep);
                        }
                    } else {
                        setAutoplay(false);
                    }
                }, currentStep.autoPlayDelay || autoplayDelay);

                // Cleanup: clear timer if autoplay is turned off before timeout
                return () => {
                    clearTimeout(timerId);
                };
            }
        }
    }, [currentStep, autoplay]);

    React.useEffect(() => {
        // setCurrentStep(wizardSteps[0]);
        // setTourCurrentStep(1);
    }, [wizardSteps]);

    function getProgressMeter(): React.ReactNode {
        if (isGuidedTour) {
            return getTourControls();
        }

        return (
            <div className={`flex flex-row items-center space-x-2 ${isMobile ? 'w-full text-2xl mb-4' : 'absolute top-0 right-0 -translate-y-full'}  z-50 pointer-events-none mr-2`}>
                {(!showWizardPointer && progressPercentage < 100 && !lock?.viewOnly) && (
                    <div
                        onClick={() => setShowWizardPointer(true)}
                        className={`flex flex-col items-center justify-center ${isMobile ? 'h-10 w-10' : 'h-5 w-5'} hover:cursor-pointer`}
                        style={{ pointerEvents: 'auto' }}
                    >
                        <HelpIcon className="text-wizard-pointer-foreground" tooltip='Show Wizard Pointer' />
                    </div>
                )}
                {(progressPercentage !== 100) && (
                    <><div style={{ pointerEvents: 'auto' }} className={isMobile ? "nowrap" : ""}>Charter Progress</div><div className={"flex flex-col" + (isMobile ? " w-full h-8" : " min-w-[200px] max-w-[50vw] h-6") + " m-2 rounded-full p-1 border-2 border-solid " + progressBarBorderColor + " " + progressBarBackgroundColor} style={{ pointerEvents: 'auto' }}>
                        <div className="flex grow h-6 animate-growx">
                            <div className={"flex h-full rounded-full " + progressBarColor} style={{ width: `${progressPercentage}%` }} />
                        </div>
                    </div><div className={"flex flex-col" + (isMobile ? " h-8 text-2xl" : " h-6 text-xl") + " nowrap"} style={{ pointerEvents: 'auto' }}>
                            {Math.round(progressPercentage) + "%"}
                        </div></>)}
            </div>
        )
    }

    function getTourControls(): React.ReactNode {
        if (!isGuidedTour) return null;

        function nextStep() {
            if (tourCurrentStep < wizardSteps.length) {
                setTourCurrentStep(tourCurrentStep + 1);
                const nextStep = wizardSteps[tourCurrentStep];
                if (nextStep) {
                    setCurrentStep(nextStep);
                }
            } else {
                finishTour();
            }
        }

        function previousStep() {
            if (tourCurrentStep > 1) {
                setTourCurrentStep(tourCurrentStep - 1);
                const prevStep = wizardSteps[tourCurrentStep - 2];
                if (prevStep) {
                    setCurrentStep(prevStep);
                }
            }
        }

        function finishTour() {
            setIsGuidedTour(false);
            setShowWizardPointer(false);
            clearWizardHighlight();
            allStepsCompleted();
        }

        const handleMouseDown = (e: React.MouseEvent) => {
            setIsDragging(true);

            // Get the current position of the element
            const rect = (e.currentTarget as HTMLElement).parentElement?.getBoundingClientRect();
            if (rect) {
                setDragStart({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top
                });

                // Initialize panel position on first drag to the element's current viewport position
                if (panelPosition.x === 0 && panelPosition.y === 0) {
                    setPanelPosition({
                        x: rect.left,
                        y: rect.top
                    });
                }
            }
        };

        return createPortal(
            <div
                className="flex flex-col space-y-2 p-2 bg-card rounded-md shadow-md fixed z-50"
                style={{
                    zIndex: 1000,
                    pointerEvents: 'auto',
                    right: panelPosition.x === 0 ? '1rem' : 'auto',
                    bottom: panelPosition.x === 0 ? '1rem' : 'auto',
                    left: panelPosition.x !== 0 ? `${panelPosition.x}px` : 'auto',
                    top: panelPosition.x !== 0 ? `${panelPosition.y}px` : 'auto',
                }}
            >
                <div
                    className='flex flex-row justify-between'
                    onMouseDown={handleMouseDown}
                    style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                >
                    <div className="text-lg font-medium">Step {tourCurrentStep} of {wizardSteps.length}</div>
                    <button
                        onClick={() => {
                            finishTour();
                        }}
                        className="bg-red-500 text-white rounded-md w-4 h-4 flex items-center justify-center hover:cursor-pointer"
                    >
                        <LucideX />
                    </button>
                </div>
                <div className="flex flex-row space-x-2">
                    <button
                        onClick={previousStep}
                        className="px-4 py-2 bg-table-header text-table-header-foreground rounded-md disabled:opacity-50"
                        disabled={tourCurrentStep === 1}
                    >
                        Previous
                    </button>
                    <button
                        onClick={nextStep}
                        className="px-4 py-2 bg-table-header text-table-header-foreground rounded-md"
                    >
                        {tourCurrentStep === wizardSteps.length ? "Finish" : "Next"}
                    </button>
                    <button
                        onClick={() => {
                            if (tourCurrentStep === wizardSteps.length) {
                                // restart from beginning
                                setTourCurrentStep(1);
                                const firstStep = wizardSteps[0];
                                if (firstStep) {
                                    setCurrentStep(firstStep);
                                }
                            }
                            setAutoplay(!autoplay);
                        }}
                        className={"px-4 py-2 " + (autoplay ? "bg-red-500" : "bg-wizard-pointer-background") + " text-white rounded-md"}
                    >
                        Autoplay
                    </button>
                </div>
                <div className='flex flex-row'>
                    <div className='text-sm italic'>* You can click on the highlighted areas to interact with them during the tour.</div>
                </div>
            </div>
        , document.body)
    }

    function clearWizardHighlight() {
        document.querySelectorAll('[data-wizard-highlighted]').forEach(elem => {
            (elem as HTMLElement).style.boxShadow = '';
            elem.removeAttribute('data-wizard-highlighted');
        });
    }

    function getWizardPointer(): React.ReactNode {
        if (!isGuidedTour) {
            if (progressPercentage === 100) {
                clearWizardHighlight();
                return null;
            }

            if (lock?.viewOnly) {
                clearWizardHighlight();
                return null;
            }

            if (!showWizardPointer) return null;
        }

        function getPointerTriangle(direction: "left" | "right" | "top" | "bottom"): React.ReactNode {
            if (direction === "left" || direction === "right") {
                return (
                    <div
                        className="flex flex-col my-auto"
                        style={{
                            width: 0,
                            height: 0,
                            borderTop: '10px solid transparent',
                            borderBottom: '10px solid transparent',
                            ...(currentStep.displaySide === 'left'
                                ?
                                {
                                    borderLeft: '16px solid hsl(var(--wizard-pointer-background))',
                                    borderRight: 'none',
                                }
                                :
                                {
                                    borderRight: '16px solid hsl(var(--wizard-pointer-background))',
                                    borderLeft: 'none',
                                }),
                        }}
                    />
                );
            } else {
                return (
                    <div
                        className="flex flex-row mx-auto"
                        style={{
                            width: 0,
                            height: 0,
                            borderLeft: '10px solid transparent',
                            borderRight: '10px solid transparent',
                            ...(currentStep.displaySide === 'bottom'
                                ?
                                {
                                    borderTop: 'none',
                                    borderBottom: '16px solid hsl(var(--wizard-pointer-background))',
                                }
                                :
                                {
                                    borderBottom: 'none',
                                    borderTop: '16px solid hsl(var(--wizard-pointer-background))',
                                }),
                        }}
                    />
                );
            }
        }

        function getContentElement(): React.ReactNode {
            const fixedWidth = (currentStep.stepDescription && typeof currentStep.stepDescription === 'string') ? true : false;
            return (
                <div
                    onClick={() => setShowWizardPointer(false)}
                    className={`flex flex-col p-2 bg-wizard-pointer-background text-wizard-pointer-foreground rounded-md ${isMobile ? "w-full" : fixedWidth ? "w-[240px]" : "w-auto"}`}
                    style={{ pointerEvents: 'auto' }}
                >
                    <h1 className={`${isMobile ? "text-2xl" : "text-lg"} font-semibold text-center`}>{currentStep.stepName}</h1>
                    <div className={`${isMobile ? "text-xl" : ""}`}>{currentStep.stepDescription}</div>
                    {currentStep.moreInfo && <div>
                        <div className={`w-min ml-auto text-nowrap ${isMobile ? "text-xl" : "text-sm"} cursor-pointer`} onClick={(e) => { e.stopPropagation(); setShowMore(!showMore); }}>{showMore ? "- Hide -" : "- Show More -"}</div>
                        {showMore &&
                            currentStep.moreInfo
                        }
                    </div>}
                </div>
            )
        }

        function getStyleBasedOnDirection(): string {
            switch (currentStep.displaySide) {
                case 'left':
                    return (showMore ? "" : "animate-bounceRight hover:animate-none ") + "flex flex-row shadow-[rgba(0,0,15,0.3)_-20px_5px_5px_0px]";
                case 'right':
                    return (showMore ? "" : "animate-bounceLeft hover:animate-none ") + "flex flex-row shadow-[rgba(0,0,15,0.3)_20px_5px_5px_0px]";
                case 'top':
                    return (showMore ? "" : "animate-bounceDown hover:animate-none ") + "flex flex-col shadow-[rgba(0,0,15,0.3)_0px_-20px_5px_0px]";
                case 'bottom':
                    return (showMore ? "" : "animate-bounceUp hover:animate-none ") + "flex flex-col shadow-[rgba(0,0,15,0.3)_0px_20px_5px_0px]";
                default:
                    return "";
            }
        }

        const wizardPointerElement = (
            <div
                id="wizard-pointer"
                className={getStyleBasedOnDirection() + (isMobile ? " w-full" : "")}
                style={{
                    position: 'absolute',
                    pointerEvents: 'none',
                    zIndex: currentStep.showAboveDialog ? 99999 : 10,
                }}
                ref={el => {
                    if (el && currentStep.stepComponentId) {
                        const target = currentStep.useClassSelector ?
                            document.getElementsByClassName(currentStep.stepComponentId)[currentStep.classSelectorNumber || 0] as HTMLElement :
                            document.getElementById(currentStep.stepComponentId);
                        if (target) {
                            // Add white border to the current step component
                            target.style.boxShadow = '0 0 0 3px hsl(var(--wizard-highlight)), 0 0 12px 5px hsl(var(--wizard-highlight))';
                            target.style.transition = 'box-shadow 0.2s';
                            // Optionally, remove border from other elements
                            document.querySelectorAll('[data-wizard-highlighted]').forEach(elem => {
                                if (elem !== target) {
                                    (elem as HTMLElement).style.boxShadow = '';
                                    elem.removeAttribute('data-wizard-highlighted');
                                }
                            });
                            target.setAttribute('data-wizard-highlighted', 'true');
                            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            target.focus();

                            const rect = target.getBoundingClientRect();
                            const pointerRect = el.getBoundingClientRect();
                            const fixedOffset = 12 * zoomLevel; // space between target and pointer;

                            let top: number;
                            let left: number;

                            if (currentStep.showAboveDialog) {
                                // For fixed positioning (portal), use viewport coordinates
                                if (currentStep.displaySide === 'left' || currentStep.displaySide === 'right') {
                                    top = rect.top + (rect.height / 2) - (pointerRect.height / 2);
                                } else if (currentStep.displaySide === 'top') {
                                    top = rect.top - pointerRect.height - fixedOffset;
                                } else {
                                    top = rect.bottom + fixedOffset;
                                }

                                if (currentStep.displaySide === 'left') {
                                    left = rect.left - pointerRect.width - fixedOffset;
                                } else if (currentStep.displaySide === 'right') {
                                    left = rect.right + fixedOffset;
                                } else { // top or bottom, center horizontally
                                    left = rect.left + (rect.width / 2) - (pointerRect.width / 2);
                                }
                            } else {
                                // For absolute positioning, compute relative to positioned parent
                                const targetDocTop = rect.top + (window.scrollY || window.pageYOffset);
                                const targetDocLeft = rect.left + (window.scrollX || window.pageXOffset);

                                const offsetParent = el.offsetParent as HTMLElement | null;
                                const parentRect = offsetParent ? offsetParent.getBoundingClientRect() : { top: 0, left: 0 };
                                const parentDocTop = (offsetParent ? parentRect.top + (window.scrollY || window.pageYOffset) : 0);
                                const parentDocLeft = (offsetParent ? parentRect.left + (window.scrollX || window.pageXOffset) : 0);

                                if (currentStep.displaySide === 'left' || currentStep.displaySide === 'right') {
                                    top = targetDocTop + (rect.height / 2) - (pointerRect.height / 2) - parentDocTop;
                                } else if (currentStep.displaySide === 'top') {
                                    top = targetDocTop - pointerRect.height - fixedOffset - parentDocTop;
                                } else {
                                    top = targetDocTop + rect.height + fixedOffset - parentDocTop;
                                }

                                if (currentStep.displaySide === 'left') {
                                    left = targetDocLeft - pointerRect.width - fixedOffset - parentDocLeft;
                                } else if (currentStep.displaySide === 'right') {
                                    left = targetDocLeft + rect.width + fixedOffset - parentDocLeft;
                                } else { // top or bottom, center horizontally
                                    left = targetDocLeft + (rect.width / 2) - (pointerRect.width / 2) - parentDocLeft;
                                }
                            }

                            // Adjust for zoom level
                            top = top / zoomLevel;
                            left = isMobile ? 0 : left / zoomLevel;
                            el.style.top = `${Math.round(top)}px`;
                            el.style.left = `${Math.round(left)}px`;
                        }
                    }
                }}
            >
                {(currentStep.displaySide === 'left' || currentStep.displaySide === 'top') && getContentElement()}
                {getPointerTriangle(currentStep.displaySide)}
                {(currentStep.displaySide === 'right' || currentStep.displaySide === 'bottom') && getContentElement()}
            </div>
        );

        // Use portal when showing above dialog to escape stacking contexts
        return currentStep.showAboveDialog ? createPortal(wizardPointerElement, document.body) : wizardPointerElement;
    }

    function updateWizardStepByElementId(stepComponentId: string, isCompleted: boolean) {
        setWizardSteps(prevSteps => {
            const updatedSteps = prevSteps.map((step) => {
                if (step.stepComponentId === stepComponentId) {
                    return { ...step, isCompleted };
                }
                return step;
            });
            // Update progress and step after state update
            setTimeout(() => {
                updateProgressPercentage(updatedSteps);
            }, 0);
            return updatedSteps;
        });
    }

    function updateProgressPercentage(steps = wizardSteps) {
        const completedSteps = steps.filter(step => step.isCompleted).length;
        const percentage = (completedSteps / steps.length) * 100;
        setProgressPercentage(percentage);
    }

    function getNextStep(steps = wizardSteps) {
        // find lowest step that is not completed sorting by stepId
        const nextStep = steps
            .filter(step => !step.isCompleted)
            .sort((a, b) => a.stepId - b.stepId)[0];

        if (nextStep) {
            setCurrentStep(nextStep);
            setShowWizardPointer(true);
        }
    }

    function allStepsCompleted() {
        setShowWizardPointer(false);

        if (isGuidedTour) {
            guidedTourCompleteFunctionCall();
            return;
        }

        if (wizardCompleteFunctionCall && !lock?.viewOnly) {
            wizardCompleteFunctionCall();
        }
    }

    function startGuidedTour() {
        setIsGuidedTour(true);
        setTourCurrentStep(1);
        const firstStep = wizardSteps[0];
        if (firstStep) {
            setCurrentStep(firstStep);
            setShowWizardPointer(true);
        }
    }

    return (
        <WizardContext.Provider value={{ getProgressMeter, updateWizardStepByElementId, getWizardPointer, wizardSteps, setWizardSteps, startGuidedTour, getTourControls, setWizardCompleteFunctionCall, setGuidedTourCompleteFunctionCall, currentStep, showWizardPointer }}>
            {children}
        </WizardContext.Provider>
    )
}

export default WizardProvider

interface WizardContextProps {
    getProgressMeter: () => React.ReactNode;
    updateWizardStepByElementId: (stepComponentId: string, isCompleted: boolean) => void;
    getWizardPointer: () => React.ReactNode;
    wizardSteps: WizardStep[];
    setWizardSteps: React.Dispatch<React.SetStateAction<WizardStep[]>>;
    startGuidedTour: () => void;
    getTourControls: () => React.ReactNode;
    setWizardCompleteFunctionCall: React.Dispatch<SetStateAction<() => void>>;
    setGuidedTourCompleteFunctionCall: React.Dispatch<SetStateAction<() => void>>;
    currentStep: WizardStep;
    showWizardPointer: boolean;
}

export const WizardContext = React.createContext<WizardContextProps>({
    getProgressMeter: () => <></>,
    updateWizardStepByElementId: () => { },
    getWizardPointer: () => <></>,
    wizardSteps: [],
    setWizardSteps: () => { },
    startGuidedTour: () => { },
    getTourControls: () => <></>,
    setWizardCompleteFunctionCall: () => { },
    setGuidedTourCompleteFunctionCall: () => { },
    currentStep: { stepId: 0, stepName: '', stepDescription: '', stepComponentId: '', displaySide: 'right', isCompleted: false, useClassSelector: false },
    showWizardPointer: false,
});